/**
 * CSV serialization utility.
 *
 * Produces RFC 4180-compliant output:
 *   - Comma-separated values
 *   - CRLF (`\r\n`) line endings per RFC 4180 § 2.1. Bumped from LF
 *     2026-07-02 (Georgetown feedback: exports were "condensed into
 *     one or two rows" on some machines). Root cause: Excel on
 *     Windows treats bare LF as an in-cell newline, so every logical
 *     row collapsed into column A of a single Excel row. CRLF fixes
 *     that; Google Sheets + Numbers already accept both flavors.
 *   - Cells containing comma, quote, CR, or LF are double-quoted
 *   - Inner double quotes are escaped by doubling (`"` → `""`)
 *
 * Keep this dependency-free so it can run in the browser, in tests,
 * and (eventually) in a future export-API worker without pulling in
 * a CSV library.
 */

export type CsvCell = string | number | boolean | null | undefined;

export interface CsvColumn<Row> {
  /** Heading printed in the first row. */
  readonly label: string;
  /** Cell value getter for each row. */
  readonly value: (row: Row) => CsvCell;
}

function escapeCell(cell: CsvCell): string {
  if (cell === null || cell === undefined) return '';
  const str = String(cell);
  // Cells with comma, double quote, or any newline need quoting per
  // RFC 4180. Inner `"` characters are escaped by doubling them.
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// RFC 4180 § 2.1 record separator. Also required by Excel on Windows,
// which otherwise treats a bare LF as an in-cell line break and
// collapses every logical row into a single Excel row.
const CRLF = '\r\n';

export function toCsv<Row>(rows: ReadonlyArray<Row>, columns: ReadonlyArray<CsvColumn<Row>>): string {
  const header = columns.map((c) => escapeCell(c.label)).join(',');
  const body = rows
    .map((row) => columns.map((c) => escapeCell(c.value(row))).join(','))
    .join(CRLF);
  return body ? `${header}${CRLF}${body}` : header;
}
